/**
 * tourSteps.js — the scripted LiTeCAB Copilot walkthrough.
 *
 * Pure data: an ordered array of steps the WalkthroughProvider advances through.
 * Each step's optional `before` runs ON ENTRY and sets up the screen so the
 * step's spotlight target is visible — navigation, loading the demo project,
 * running analysis. These run through the SAME action bridge + tool registry the
 * LLM agent uses, so the tour reuses existing, tested code paths rather than
 * duplicating project-loading or analysis logic. `before` fns are idempotent so
 * Back/Next both work, and the provider runs them best-effort (failures never
 * crash the tour — the bubble just degrades to a centered card).
 *
 * Step shape: { id, screen, coachId, title, body, before? }
 *   - screen : 'startup' | 'project' (informational)
 *   - coachId: a `data-coach` anchor to spotlight, or null for a centered bubble
 *   - before : optional async fn run before the step is shown
 */

import { callAction } from '../agent/actionBridge';
import { runTool } from '../agent/toolRegistry';

const settle = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Anchors referenced below. Kept here so a unit test can assert every step's
// coachId is a real anchor the app actually renders.
export const KNOWN_COACH_IDS = [
  'start-options',
  'start-batpac',
  'start-samples',
  'start-load',
  'start-templates',
  'start-ai',
  'batpac-designs',
  'panel-diagram',
  'tab-inventory',
  'tab-analysis',
  'btn-add-process',
  'panel-design',
  'impact-source',
];

export const DEMO_TEMPLATE_ID = 'nmc811_cell_manufacturing.json';
// The demo project's saved id, shared by every run of the tour.
export const DEMO_PROJECT_ID = 'tour-demo-nmc811';

export const TOUR_STEPS = [
  {
    id: 'welcome',
    screen: 'startup',
    coachId: null,
    title: "Hi — I'm the LiTeCAB Copilot 👋",
    body: "Let me give you a quick 60-second tour of how LiTeCAB works. I'll load a demo project and run a real analysis so you can see it end-to-end. You can exit anytime.",
  },
  {
    id: 'new-project',
    screen: 'startup',
    coachId: 'start-options',
    title: 'Start a project',
    body: 'Three ways in: design a cell with BatPaC, open a sample, or describe your own process and let me draft it. All three end in the same kind of project and the same results.',
    before: () => callAction('startupNavigate', { screen: 'landing' }),
  },
  {
    id: 'batpac',
    screen: 'startup',
    coachId: 'batpac-designs',
    title: 'Design with BatPaC',
    body: "Pick a cathode, type your own capacity or voltage if you have them, and BatPaC sets how much of each material goes into the cell. Each design shows its Wh/kg, carbon and cost per kWh against a baseline.",
    before: () => callAction('startupNavigate', { screen: 'batpac' }),
  },
  {
    id: 'templates',
    screen: 'startup',
    coachId: 'start-templates',
    title: 'Or open a sample',
    body: 'These samples come with every input linked to carbon and price data. Open one and edit it.',
    before: () => callAction('startupNavigate', { screen: 'samples' }),
  },
  {
    id: 'diagram',
    screen: 'project',
    coachId: 'panel-diagram',
    title: 'Your process flow',
    body: "I've opened the NMC811 cell-manufacturing demo in Build mode. Each box is a manufacturing step, connected in the order they happen. Click any step to open its editor here.",
    // Load the demo (reuses the agent's create_project_from_template tool) and
    // show the Build mode (the diagram canvas). The fixed id makes a replay (or
    // Back then Next) replace the earlier demo instead of saving another copy.
    before: async () => {
      await runTool('create_project_from_template', { templateId: DEMO_TEMPLATE_ID, name: 'NMC811 (demo)', projectId: DEMO_PROJECT_ID });
      await callAction('setView', { left: 'diagram', mode: 'build' });
    },
  },
  {
    id: 'inventory',
    screen: 'project',
    coachId: 'tab-inventory',
    title: 'Inventory mode',
    body: 'Inventory mode lists every material and energy input across the whole project — with quantities and costs. Prices carry their source; ask me to find a current market price for any input and I\'ll cite where it came from. (To edit one step\'s inputs, open it in Build mode.)',
    before: () => callAction('setView', { mode: 'inventory' }),
  },
  {
    id: 'analysis',
    screen: 'project',
    coachId: 'tab-analysis',
    title: 'Run the analysis',
    body: 'Results mode runs LCA (environmental impact), TEA (cost & revenue), and circularity. Hit Next and I\'ll run them all so you can see real numbers…',
    before: () => callAction('setView', { mode: 'results' }),
  },
  {
    id: 'results',
    screen: 'project',
    coachId: 'tab-analysis',
    title: 'Your results',
    body: "Done! You're now looking at real numbers — climate impact, total cost, and revenue for this project. Want any of it explained in plain language? Just ask me in the chat — I can also edit the project and re-run for you.",
    before: () => runTool('run_all', {}),
  },
  {
    id: 'impact-data',
    screen: 'project',
    coachId: 'impact-source',
    title: 'Choose your impact data',
    body: "Impact scores come from ecoinvent 3.11 by default. Switch here to GREET 2025, Argonne's open dataset, and the LCA re-runs on it; any input GREET can't cover is listed rather than silently filled in. You can also ask me to compare the two.",
    // The select sits in the LCA tab of Results, which run_all leaves open.
    before: () => callAction('setView', { mode: 'results' }),
  },
  {
    id: 'design',
    screen: 'project',
    coachId: 'panel-design',
    title: 'Try a different cell design',
    body: "Move a slider and the Wh/kg, carbon and cost per kWh follow. Apply updates the quantities and re-runs the results. All settings has the rest, or ask me, e.g. \"make the cathode 120 µm\".",
    before: () => callAction('openDesignPanel'),
  },
  {
    id: 'finish',
    screen: 'startup',
    coachId: 'start-options',
    title: "That's the tour 🎉",
    body: "Now it's your turn. Design a cell with BatPaC, open a sample, or describe your own process and I'll draft it. I'm always one click away in the bottom-right corner whenever you need help.",
    // Return to the start screen (the demo project is throwaway).
    before: async () => {
      await callAction('goToStartup', {});
      await settle(140); // let ProjectStartup remount + re-register its bridge action
      await callAction('startupNavigate', { screen: 'landing' });
    },
  },
];

export default TOUR_STEPS;
